<template>
  <div class="user-statistics">
    <Spin v-if="loading" fix></Spin>

    <!-- 统计概览卡片 -->
    <Row :gutter="16" class="stats-overview">
      <Col span="6" v-for="item in overviewCards" :key="item.key">
        <Card class="stat-card">
          <div class="stat-icon" :style="{ backgroundColor: item.color }">
            <Icon :type="item.icon" size="24" color="#fff" />
          </div>
          <div class="stat-content">
            <div class="stat-value">{{ formatNumber(item.value) }}</div>
            <div class="stat-label">{{ item.label }}</div>
            <div class="stat-trend" v-if="item.growth !== undefined">
              <span :class="item.growth >= 0 ? 'up' : 'down'">
                <Icon :type="item.growth >= 0 ? 'ios-arrow-up' : 'ios-arrow-down'" />
                {{ Math.abs(item.growth) }}%
              </span>
              <span class="trend-desc">较上期</span>
            </div>
          </div>
        </Card>
      </Col>
    </Row>

    <!-- 图表区域 -->
    <Row :gutter="16" style="margin-top: 16px;">
      <Col span="16">
        <Card title="用户增长趋势">
          <div ref="userGrowthChart" style="height: 350px;"></div>
        </Card>
      </Col>
      <Col span="8">
        <Card title="用户活跃度分布">
          <div ref="userActivityChart" style="height: 350px;"></div>
        </Card>
      </Col>
    </Row>

    <Row :gutter="16" style="margin-top: 16px;">
      <Col span="12">
        <Card title="用户留存率">
          <div ref="retentionChart" style="height: 300px;"></div>
        </Card>
      </Col>
      <Col span="12">
        <Card title="用户价值分析(RFM)">
          <div ref="rfmChart" style="height: 300px;"></div>
        </Card>
      </Col>
    </Row>

    <!-- 用户数据表格 -->
    <Card title="用户详细统计" style="margin-top: 16px;">
      <Table :columns="tableColumns" :data="tableData" border stripe />
    </Card>
  </div>
</template>

<script>
import { getUserStatistics } from '@/api/statistics'
import Echarts from 'echarts'

export default {
  name: 'UserStatistics',
  props: {
    dateRange: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      loading: false,
      statsData: null,
      userGrowthChart: null,
      userActivityChart: null,
      retentionChart: null,
      rfmChart: null,
      tableColumns: [
        { title: '日期', key: 'date', width: 120 },
        { title: '新增用户', key: 'newUsers', width: 100 },
        { title: '活跃用户', key: 'activeUsers', width: 100 },
        { title: '累计用户', key: 'totalUsers', width: 100 },
        { title: '活跃率', key: 'activeRate', width: 100, render: (h, p) => h('span', `${(p.row.activeRate * 100).toFixed(1)}%`) },
        { title: '下单用户', key: 'orderUsers', width: 100 },
        { title: '人均订单', key: 'avgOrders', width: 100, render: (h, p) => h('span', p.row.avgOrders.toFixed(2)) }
      ],
      tableData: []
    }
  },
  computed: {
    overviewCards() {
      if (!this.statsData) return []
      const data = this.statsData
      return [
        {
          key: 'total',
          label: '总用户数',
          value: data.totalUsers || 0,
          growth: data.userGrowth,
          icon: 'ios-people',
          color: '#409EFF'
        },
        {
          key: 'new',
          label: '新增用户',
          value: data.newUsers || 0,
          growth: data.newUserGrowth,
          icon: 'ios-person-add',
          color: '#67C23A'
        },
        {
          key: 'active',
          label: '活跃用户',
          value: data.activeUsers || 0,
          growth: data.activeGrowth,
          icon: 'ios-pulse',
          color: '#E6A23C'
        },
        {
          key: 'retention',
          label: '7日留存率',
          value: `${((data.retentionRate || 0) * 100).toFixed(1)}%`,
          growth: data.retentionGrowth,
          icon: 'ios-refresh',
          color: '#909399'
        }
      ]
    }
  },
  watch: {
    dateRange: {
      handler() {
        this.loadData()
      },
      deep: true
    }
  },
  methods: {
    async loadData() {
      this.loading = true
      try {
        const params = {
          startDate: this.dateRange[0],
          endDate: this.dateRange[1]
        }
        
        const res = await getUserStatistics(params)
        if (res.data && (res.data.success || res.data.code === 200)) {
          this.statsData = res.data.data
          this.tableData = this.statsData.dailyStats || []
          this.$nextTick(() => {
            this.initCharts()
          })
        } else {
          this.$Message.error(res.data?.message || '获取用户统计数据失败')
        }
      } catch (error) {
        this.$Message.error('获取用户统计数据失败')
        console.error(error)
      } finally {
        this.loading = false
      }
    },
    initCharts() {
      this.initUserGrowthChart()
      this.initUserActivityChart()
      this.initRetentionChart()
      this.initRfmChart()
    },
    initUserGrowthChart() {
      if (!this.$refs.userGrowthChart || !this.statsData) return
      
      if (this.userGrowthChart) {
        this.userGrowthChart.dispose()
      }
      
      this.userGrowthChart = Echarts.init(this.$refs.userGrowthChart)
      const growthData = this.statsData.userGrowthTrend || []
      
      const option = {
        tooltip: {
          trigger: 'axis',
          axisPointer: { type: 'cross' }
        },
        legend: {
          data: ['新增用户', '活跃用户', '累计用户']
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '3%',
          containLabel: true
        },
        xAxis: {
          type: 'category',
          boundaryGap: false,
          data: growthData.map(item => item.date)
        },
        yAxis: [
          {
            type: 'value',
            name: '用户数',
            position: 'left'
          },
          {
            type: 'value',
            name: '累计用户',
            position: 'right'
          }
        ],
        series: [
          {
            name: '新增用户',
            type: 'bar',
            data: growthData.map(item => item.newUsers),
            itemStyle: { color: '#67C23A' }
          },
          {
            name: '活跃用户',
            type: 'bar',
            data: growthData.map(item => item.activeUsers),
            itemStyle: { color: '#E6A23C' }
          },
          {
            name: '累计用户',
            type: 'line',
            yAxisIndex: 1,
            smooth: true,
            data: growthData.map(item => item.totalUsers),
            itemStyle: { color: '#409EFF' }
          }
        ]
      }
      
      this.userGrowthChart.setOption(option)
    },
    initUserActivityChart() {
      if (!this.$refs.userActivityChart || !this.statsData) return
      
      if (this.userActivityChart) {
        this.userActivityChart.dispose()
      }
      
      this.userActivityChart = Echarts.init(this.$refs.userActivityChart)
      const activityData = this.statsData.activityDistribution || []
      
      const option = {
        tooltip: {
          trigger: 'item',
          formatter: '{b}: {c} ({d}%)'
        },
        legend: {
          orient: 'vertical',
          right: 10,
          top: 20,
          data: activityData.map(item => item.name)
        },
        series: [
          {
            type: 'pie',
            radius: ['40%', '70%'],
            avoidLabelOverlap: false,
            itemStyle: {
              borderRadius: 10,
              borderColor: '#fff',
              borderWidth: 2
            },
            label: {
              show: false
            },
            emphasis: {
              label: {
                show: true,
                fontSize: 14,
                fontWeight: 'bold'
              }
            },
            data: activityData
          }
        ],
        color: ['#F56C6C', '#E6A23C', '#409EFF', '#67C23A']
      }
      
      this.userActivityChart.setOption(option)
    },
    initRetentionChart() {
      if (!this.$refs.retentionChart || !this.statsData) return
      
      if (this.retentionChart) {
        this.retentionChart.dispose()
      }
      
      this.retentionChart = Echarts.init(this.$refs.retentionChart)
      const retentionData = this.statsData.retentionData || []
      
      const option = {
        tooltip: {
          trigger: 'axis',
          formatter: (params) => {
            return `${params[0].axisValue}<br/>留存率: ${(params[0].value * 100).toFixed(1)}%`
          }
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '3%',
          containLabel: true
        },
        xAxis: {
          type: 'category',
          data: retentionData.map(item => item.day)
        },
        yAxis: {
          type: 'value',
          max: 1,
          axisLabel: {
            formatter: (value) => `${(value * 100).toFixed(0)}%`
          }
        },
        series: [
          {
            type: 'line',
            smooth: true,
            data: retentionData.map(item => item.rate),
            itemStyle: { color: '#409EFF' },
            areaStyle: {
              color: {
                type: 'linear',
                x: 0, y: 0, x2: 0, y2: 1,
                colorStops: [
                  { offset: 0, color: 'rgba(64,158,255,0.3)' },
                  { offset: 1, color: 'rgba(64,158,255,0.05)' }
                ]
              }
            },
            markLine: {
              data: [
                { type: 'average', name: '平均值' }
              ]
            }
          }
        ]
      }
      
      this.retentionChart.setOption(option)
    },
    initRfmChart() {
      if (!this.$refs.rfmChart || !this.statsData) return
      
      if (this.rfmChart) {
        this.rfmChart.dispose()
      }
      
      this.rfmChart = Echarts.init(this.$refs.rfmChart)
      const rfmData = this.statsData.rfmData || []
      
      const option = {
        tooltip: {
          trigger: 'axis',
          axisPointer: { type: 'shadow' }
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '3%',
          containLabel: true
        },
        xAxis: {
          type: 'category',
          data: rfmData.map(item => item.segment)
        },
        yAxis: {
          type: 'value'
        },
        series: [
          {
            type: 'bar',
            data: rfmData.map((item, index) => ({
              value: item.count,
              itemStyle: {
                color: ['#67C23A', '#409EFF', '#E6A23C', '#F56C6C', '#909399'][index % 5]
              }
            })),
            barWidth: '50%'
          }
        ]
      }
      
      this.rfmChart.setOption(option)
    },
    formatNumber(value) {
      if (typeof value === 'string') return value
      if (value >= 10000) {
        return (value / 10000).toFixed(1) + '万'
      }
      return value
    }
  },
  mounted() {
    this.loadData()
    window.addEventListener('resize', () => {
      this.userGrowthChart?.resize()
      this.userActivityChart?.resize()
      this.retentionChart?.resize()
      this.rfmChart?.resize()
    })
  },
  beforeDestroy() {
    this.userGrowthChart?.dispose()
    this.userActivityChart?.dispose()
    this.retentionChart?.dispose()
    this.rfmChart?.dispose()
  }
}
</script>

<style lang="less" scoped>
.user-statistics {
  .stats-overview {
    .stat-card {
      display: flex;
      align-items: center;
      padding: 20px;
      
      .stat-icon {
        width: 60px;
        height: 60px;
        border-radius: 12px;
        display: flex;
        align-items: center;
        justify-content: center;
        margin-right: 16px;
      }
      
      .stat-content {
        flex: 1;
        
        .stat-value {
          font-size: 28px;
          font-weight: bold;
          color: #2c3e50;
          line-height: 1.2;
        }
        
        .stat-label {
          font-size: 14px;
          color: #909399;
          margin-top: 4px;
        }
        
        .stat-trend {
          font-size: 12px;
          margin-top: 8px;
          
          .up {
            color: #67C23A;
          }
          
          .down {
            color: #F56C6C;
          }
          
          .trend-desc {
            color: #909399;
            margin-left: 4px;
          }
        }
      }
    }
  }
}
</style>

